import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Container, Typography, Button, Paper, Tabs, Tab, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, CircularProgress, IconButton, Badge,
  TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch, 
  Divider, Alert, InputAdornment
} from '@mui/material';
import { 
  Mail as MailIcon, 
  SmartToy as AIIcon, 
  Visibility, 
  VisibilityOff, 
  Save as SaveIcon,
  Settings as SettingsIcon 
} from '@mui/icons-material';
import { getUserForecasts } from '../../utils/forecastService';
import { getAllUsers, updateUserRole } from '../../utils/userService';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { subscribeToAllMessages } from '../../utils/messageService';
import {
  getAgentConfig,
  saveAgentConfig,
  defaultCustomerSupportConfig,
  defaultClassifierConfig,
  defaultReportGeneratorConfig,
  defaultCareerAnalyzerConfig,
  defaultCareerSuggesterConfig,
  defaultResumeParserConfig,
  defaultPivotFeasibilityConfig,
  defaultSocMapperConfig,
  defaultTrainingPlannerConfig,
  defaultQualityAuditorConfig,
} from '../../utils/aiAgentService';
import { getPlatformSettings, updatePlatformSettings } from '../../utils/platformSettingsService';
import ArchetypesManager from './ArchetypesManager';

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [forecasts, setForecasts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [stats, setStats] = useState({
    totalForecasts: 0,
    completedForecasts: 0,
    processingForecasts: 0,
    totalUsers: 0,
    adminUsers: 0
  });
  
  // Platform Settings state
  const [platformSettings, setPlatformSettings] = useState(null);
  const [platformSaving, setPlatformSaving] = useState(false);
  const [platformStatus, setPlatformStatus] = useState(null);
  
  // AI Agents state
  const [aiAgentTab, setAiAgentTab] = useState(0); // 0=Platform, 1=Cognitive, 2=Career
  const [supportConfig, setSupportConfig] = useState(defaultCustomerSupportConfig);
  const [classifierConfig, setClassifierConfig] = useState(defaultClassifierConfig);
  const [reportGeneratorConfig, setReportGeneratorConfig] = useState(defaultReportGeneratorConfig);
  const [careerAnalyzerConfig, setCareerAnalyzerConfig] = useState(defaultCareerAnalyzerConfig);
  const [careerSuggesterConfig, setCareerSuggesterConfig] = useState(defaultCareerSuggesterConfig);
  const [resumeParserConfig, setResumeParserConfig] = useState(defaultResumeParserConfig);
  const [pivotFeasibilityConfig, setPivotFeasibilityConfig] = useState(defaultPivotFeasibilityConfig);
  const [socMapperConfig, setSocMapperConfig] = useState(null);
  const [trainingPlannerConfig, setTrainingPlannerConfig] = useState(defaultTrainingPlannerConfig);
  const [qualityAuditorConfig, setQualityAuditorConfig] = useState(defaultQualityAuditorConfig);
  const [showSupportKey, setShowSupportKey] = useState(false);
  const [showClassifierKey, setShowClassifierKey] = useState(false);
  const [showReportGeneratorKey, setShowReportGeneratorKey] = useState(false);
  const [showCareerAnalyzerKey, setShowCareerAnalyzerKey] = useState(false);
  const [showSuggesterKey, setShowSuggesterKey] = useState(false);
  const [showParserKey, setShowParserKey] = useState(false);
  const [showPivotKey, setShowPivotKey] = useState(false);
  const [showSocMapperKey, setShowSocMapperKey] = useState(false);
  const [showTrainingPlannerKey, setShowTrainingPlannerKey] = useState(false);
  const [showQualityAuditorKey, setShowQualityAuditorKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  
  // Platform Settings (API Keys, Pricing) state
  const [rapidApiKey, setRapidApiKey] = useState('');
  const [rapidApiKeySaved, setRapidApiKeySaved] = useState(''); // For displaying masked version
  
  // Centralized AI API Keys (used by all agents)
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [openaiApiKeySaved, setOpenaiApiKeySaved] = useState('');
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [anthropicApiKeySaved, setAnthropicApiKeySaved] = useState('');
  const [xaiApiKey, setXaiApiKey] = useState('');
  const [xaiApiKeySaved, setXaiApiKeySaved] = useState('');
  const [showOpenaiKey, setShowOpenaiKey] = useState(false);
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);
  const [showXaiKey, setShowXaiKey] = useState(false);
  
  // Stripe - Sandbox mode
  const [stripeSandboxSecretKey, setStripeSandboxSecretKey] = useState('');
  const [stripeSandboxWebhookSecret, setStripeSandboxWebhookSecret] = useState('');
  const [stripeSandboxSecretSaved, setStripeSandboxSecretSaved] = useState('');
  const [stripeSandboxWebhookSaved, setStripeSandboxWebhookSaved] = useState('');
  
  // Stripe - Live mode
  const [stripeLiveSecretKey, setStripeLiveSecretKey] = useState('');
  const [stripeLiveWebhookSecret, setStripeLiveWebhookSecret] = useState('');
  const [stripeLiveSecretSaved, setStripeLiveSecretSaved] = useState('');
  const [stripeLiveWebhookSaved, setStripeLiveWebhookSaved] = useState('');
  
  // Stripe mode toggle
  const [stripeMode, setStripeMode] = useState('sandbox'); // 'sandbox' or 'live'
  
  // Pricing
  const [reportPrice, setReportPrice] = useState(79);
  const [careerReportPrice, setCareerReportPrice] = useState(79);
  const [bundlePrice, setBundlePrice] = useState(149);
  
  // UI state
  const [showRapidApiKey, setShowRapidApiKey] = useState(false);
  const [showStripeSandboxSecret, setShowStripeSandboxSecret] = useState(false);
  const [showStripeSandboxWebhook, setShowStripeSandboxWebhook] = useState(false);
  const [showStripeLiveSecret, setShowStripeLiveSecret] = useState(false);
  const [showStripeLiveWebhook, setShowStripeLiveWebhook] = useState(false);
  
  // Saving states for individual sections
  const [savingPricing, setSavingPricing] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);
  const [savingRapidApi, setSavingRapidApi] = useState(false);
  const [pricingStatus, setPricingStatus] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [rapidApiStatus, setRapidApiStatus] = useState(null);
  
  // Legacy - keep for compatibility
  const [platformSettingsSaving, setPlatformSettingsSaving] = useState(false);
  const [platformSettingsStatus, setPlatformSettingsStatus] = useState(null);
  
  const navigate = useNavigate();

  // Subscribe to messages for unread count
  useEffect(() => {
    const unsubscribe = subscribeToAllMessages((messages) => {
      // Count messages where user replied AFTER admin last read
      const unread = messages.filter((msg) => {
        if (msg.status === 'resolved') return false; // Skip resolved
        
        if (msg.messages && msg.messages.length > 0) {
          const lastMessage = msg.messages[msg.messages.length - 1];
          
          // Only count if last message is from user
          if (lastMessage.from === 'user') {
            const lastMessageTime = new Date(lastMessage.timestamp).getTime();
            const lastReadTime = msg.lastReadByAdmin 
              ? (msg.lastReadByAdmin.toDate ? msg.lastReadByAdmin.toDate().getTime() : new Date(msg.lastReadByAdmin).getTime())
              : 0;
            
            // Unread if user message is newer than last read time
            return lastMessageTime > lastReadTime;
          }
        }
        return false;
      }).length;
      setUnreadMessages(unread);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 0) {
        // Load all forecasts
        const forecastsRef = collection(db, 'forecasts');
        const q = query(forecastsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const allForecasts = [];
        querySnapshot.forEach((doc) => {
          allForecasts.push({ id: doc.id, ...doc.data() });
        });
        
        setForecasts(allForecasts);
        
        setStats(prev => ({
          ...prev,
          totalForecasts: allForecasts.length,
          completedForecasts: allForecasts.filter(f => f.status === 'completed').length,
          processingForecasts: allForecasts.filter(f => f.status === 'processing').length
        }));
      } else if (activeTab === 1) {
        const allUsers = await getAllUsers();
        setUsers(allUsers);
        
        setStats(prev => ({
          ...prev,
          totalUsers: allUsers.length,
          adminUsers: allUsers.filter(u => u.role === 'admin').length
        }));
      } else if (activeTab === 3) {
        // Load platform settings
        const settings = await getPlatformSettings();
        setPlatformSettings(settings);
      } else if (activeTab === 5) {
        // Load AI agent configurations
        const support = await getAgentConfig('customer-support');
        if (support) {
          setSupportConfig(support);
        }
        
        const classifier = await getAgentConfig('classifier');
        if (classifier) {
          setClassifierConfig(classifier);
        }
        
        const reportGenerator = await getAgentConfig('report-generator');
        if (reportGenerator) {
          setReportGeneratorConfig(reportGenerator);
        }
        
        const careerAnalyzer = await getAgentConfig('career-analyzer');
        if (careerAnalyzer) {
          setCareerAnalyzerConfig(careerAnalyzer);
        }
        
        const careerSuggester = await getAgentConfig('career-suggester');
        if (careerSuggester) {
          setCareerSuggesterConfig(careerSuggester);
        }
        
        const resumeParser = await getAgentConfig('resume-parser');
        if (resumeParser) {
          setResumeParserConfig(resumeParser);
        }
        
        const pivotFeasibility = await getAgentConfig('pivot-feasibility');
        if (pivotFeasibility) {
          setPivotFeasibilityConfig(pivotFeasibility);
        }
        
        const socMapper = await getAgentConfig('soc-mapper');
        if (socMapper) {
          setSocMapperConfig(socMapper);
        } else {
          setSocMapperConfig(defaultSocMapperConfig);
        }
        
        // Load Training Planner (Grok)
        const trainingPlanner = await getAgentConfig('training-planner');
        if (trainingPlanner) {
          setTrainingPlannerConfig(trainingPlanner);
        }
        
        // Load Quality Auditor
        const qualityAuditor = await getAgentConfig('quality-auditor');
        if (qualityAuditor) {
          setQualityAuditorConfig(qualityAuditor);
        }
      }
      
      // Load platform settings (includes API keys and pricing)
      if (activeTab === 2 || activeTab === 3) {
        const settings = await getPlatformSettings();
        if (settings) {
          setPlatformSettings(settings);
          
          // RapidAPI Key
          if (settings.rapidApiKey) {
            setRapidApiKey(settings.rapidApiKey);
            setRapidApiKeySaved(settings.rapidApiKey);
          }
          
          // Centralized AI API Keys
          if (settings.openaiApiKey) {
            setOpenaiApiKey(settings.openaiApiKey);
            setOpenaiApiKeySaved(settings.openaiApiKey);
          }
          if (settings.anthropicApiKey) {
            setAnthropicApiKey(settings.anthropicApiKey);
            setAnthropicApiKeySaved(settings.anthropicApiKey);
          }
          if (settings.xaiApiKey) {
            setXaiApiKey(settings.xaiApiKey);
            setXaiApiKeySaved(settings.xaiApiKey);
          }
          
          // Stripe Sandbox Keys
          if (settings.stripeSandboxSecretKey) {
            setStripeSandboxSecretKey(settings.stripeSandboxSecretKey);
            setStripeSandboxSecretSaved(settings.stripeSandboxSecretKey);
          }
          if (settings.stripeSandboxWebhookSecret) {
            setStripeSandboxWebhookSecret(settings.stripeSandboxWebhookSecret);
            setStripeSandboxWebhookSaved(settings.stripeSandboxWebhookSecret);
          }
          
          // Stripe Live Keys
          if (settings.stripeLiveSecretKey) {
            setStripeLiveSecretKey(settings.stripeLiveSecretKey);
            setStripeLiveSecretSaved(settings.stripeLiveSecretKey);
          }
          if (settings.stripeLiveWebhookSecret) {
            setStripeLiveWebhookSecret(settings.stripeLiveWebhookSecret);
            setStripeLiveWebhookSaved(settings.stripeLiveWebhookSecret);
          }
          
          // Stripe Mode (sandbox or live)
          if (settings.stripeMode) setStripeMode(settings.stripeMode);
          
          // Legacy migration: if old keys exist, move to sandbox
          if (settings.stripeSecretKey && !settings.stripeSandboxSecretKey) {
            setStripeSandboxSecretKey(settings.stripeSecretKey);
            setStripeSandboxSecretSaved(settings.stripeSecretKey);
          }
          if (settings.stripeWebhookSecret && !settings.stripeSandboxWebhookSecret) {
            setStripeSandboxWebhookSecret(settings.stripeWebhookSecret);
            setStripeSandboxWebhookSaved(settings.stripeWebhookSecret);
          }
          
          // Pricing
          if (settings.reportPrice !== undefined) setReportPrice(settings.reportPrice);
          if (settings.careerReportPrice !== undefined) setCareerReportPrice(settings.careerReportPrice);
          if (settings.bundlePrice !== undefined) setBundlePrice(settings.bundlePrice);
        }
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserRole = async (userId, currentRole) => {
    if (!window.confirm(`Change user role to ${currentRole === 'admin' ? 'user' : 'admin'}?`)) {
      return;
    }

    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const success = await updateUserRole(userId, newRole);
    
    if (success) {
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
    } else {
      alert('Failed to update user role');
    }
  };

  // Helper function to mask API keys for display
  const maskApiKey = (key) => {
    if (!key || key.length < 8) return key ? '••••••••' : '';
    const prefix = key.substring(0, 7);
    const suffix = key.substring(key.length - 4);
    return `${prefix}...${suffix}`;
  };

  // Platform Settings handlers
  const handleSavePlatformSettings = async () => {
    setPlatformSaving(true);
    setPlatformStatus(null);
    try {
      const success = await updatePlatformSettings(platformSettings);
      if (success) {
        setPlatformStatus({ type: 'success', message: '✅ Platform settings saved!' });
      } else {
        setPlatformStatus({ type: 'error', message: '❌ Failed to save settings' });
      }
    } catch (error) {
      setPlatformStatus({ type: 'error', message: '❌ Error: ' + error.message });
    } finally {
      setPlatformSaving(false);
    }
  };
  
  // Save Pricing settings
  const handleSavePricing = async () => {
    setSavingPricing(true);
    setPricingStatus(null);
    try {
      await updatePlatformSettings({
        reportPrice,
        careerReportPrice,
        bundlePrice
      });
      setPricingStatus({ type: 'success', message: '✅ Pricing saved!' });
    } catch (error) {
      setPricingStatus({ type: 'error', message: '❌ Failed to save pricing' });
    } finally {
      setSavingPricing(false);
    }
  };
  
  // Save Stripe settings
  const handleSaveStripe = async () => {
    setSavingStripe(true);
    setStripeStatus(null);
    try {
      await updatePlatformSettings({
        stripeMode,
        stripeSandboxSecretKey,
        stripeSandboxWebhookSecret,
        stripeLiveSecretKey,
        stripeLiveWebhookSecret,
        // Legacy fields for backward compatibility - use active mode's keys
        stripeSecretKey: stripeMode === 'live' ? stripeLiveSecretKey : stripeSandboxSecretKey,
        stripeWebhookSecret: stripeMode === 'live' ? stripeLiveWebhookSecret : stripeSandboxWebhookSecret
      });
      // Update saved states for display
      setStripeSandboxSecretSaved(stripeSandboxSecretKey);
      setStripeSandboxWebhookSaved(stripeSandboxWebhookSecret);
      setStripeLiveSecretSaved(stripeLiveSecretKey);
      setStripeLiveWebhookSaved(stripeLiveWebhookSecret);
      setStripeStatus({ type: 'success', message: `✅ Stripe ${stripeMode} settings saved!` });
    } catch (error) {
      setStripeStatus({ type: 'error', message: '❌ Failed to save Stripe settings' });
    } finally {
      setSavingStripe(false);
    }
  };
  
  // Save RapidAPI settings
  const handleSaveRapidApi = async () => {
    setSavingRapidApi(true);
    setRapidApiStatus(null);
    try {
      await updatePlatformSettings({ rapidApiKey });
      setRapidApiKeySaved(rapidApiKey);
      setRapidApiStatus({ type: 'success', message: '✅ RapidAPI key saved!' });
    } catch (error) {
      setRapidApiStatus({ type: 'error', message: '❌ Failed to save RapidAPI key' });
    } finally {
      setSavingRapidApi(false);
    }
  };

  // AI Agent handlers
  const handleSaveAgentConfig = async (agentId, config) => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const success = await saveAgentConfig(agentId, config);
      if (success) {
        setSaveStatus({ type: 'success', message: `✅ ${config.name} configuration saved!` });
      } else {
        setSaveStatus({ type: 'error', message: '❌ Failed to save configuration' });
      }
    } catch (error) {
      setSaveStatus({ type: 'error', message: '❌ Error: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'payment_pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderAgentCard = (agentId, config, setConfig, showKey, setShowKey) => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AIIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 300, mb: 0.5 }}>
            {config.name}
          </Typography>
          <Typography sx={{ color: '#737373', fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {agentId === 'classifier' && 'Fast classification of cognitive patterns (GPT-4-mini)'}
            {agentId === 'report-generator' && 'Generate comprehensive cognitive reports (Claude Sonnet 4)'}
            {agentId === 'customer-support' && 'AI-powered customer support agent'}
            {agentId === 'career-suggester' && 'Suggests 3 logical next career options based on resume analysis'}
            {agentId === 'resume-parser' && 'Extracts skills, education, experience, and certifications from resumes'}
            {agentId === 'career-analyzer' && 'Analyzes job market data and creates personalized development plans'}
            {agentId === 'pivot-feasibility' && 'Assesses career pivot feasibility before expensive job market searches'}
            {agentId === 'soc-mapper' && 'Maps job titles to SOC codes when O*NET lookup fails (AI fallback for Market Verdict)'}
          </Typography>
        </Box>
        <Chip
          label={config.enabled ? 'Active' : 'Inactive'}
          color={config.enabled ? 'success' : 'default'}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      <Divider sx={{ my: 3, borderColor: '#262626' }} />

      <Box sx={{ display: 'grid', gap: 3 }}>
        {/* Model Selection */}
        <FormControl fullWidth>
          <InputLabel sx={{ color: '#737373' }}>Model</InputLabel>
          <Select
            value={config.model || 'gpt-4o'}
            onChange={(e) => {
              const model = e.target.value;
              let provider = 'openai';
              if (model.startsWith('claude')) provider = 'anthropic';
              if (model.startsWith('grok')) provider = 'xai';
              setConfig({ ...config, model, provider });
            }}
            label="Model"
            sx={{ color: '#fafafa' }}
          >
            {/* OpenAI Models */}
            <MenuItem value="gpt-4o-mini">GPT-4o Mini (Fast & Cheap)</MenuItem>
            <MenuItem value="gpt-4o">GPT-4o (Recommended)</MenuItem>
            <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
            
            {/* xAI / Grok Models */}
            <MenuItem value="grok-4-latest">Grok-4 (Web Search Enabled)</MenuItem>
            <MenuItem value="grok-3-latest">Grok-3</MenuItem>
            
            {/* Anthropic Claude 4.5 Models (Latest) */}
            <MenuItem value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5 (Latest, Best Value)</MenuItem>
            <MenuItem value="claude-opus-4-5-20251101">Claude Opus 4.5 (Most Capable)</MenuItem>
            <MenuItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (Fast)</MenuItem>
            
            {/* Anthropic Claude 4 Models */}
            <MenuItem value="claude-opus-4-1-20250805">Claude Opus 4.1</MenuItem>
            <MenuItem value="claude-sonnet-4-20250514">Claude Sonnet 4</MenuItem>
            <MenuItem value="claude-opus-4-20250514">Claude Opus 4</MenuItem>
            
            {/* Anthropic Claude 3.5/3 Models */}
            <MenuItem value="claude-3-5-haiku-20241022">Claude Haiku 3.5</MenuItem>
            <MenuItem value="claude-3-haiku-20240307">Claude Haiku 3 (Fastest)</MenuItem>
          </Select>
        </FormControl>

        {/* API Key Notice - Keys are centralized in Platform Settings */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'rgba(0,255,245,0.05)', 
          border: '1px solid rgba(0,255,245,0.2)', 
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <SettingsIcon sx={{ color: '#00fff5', fontSize: 18 }} />
          <Typography sx={{ color: '#a3a3a3', fontFamily: 'monospace', fontSize: '0.8rem' }}>
            API keys are configured in{' '}
            <span style={{ color: '#00fff5' }}>Platform Settings → AI Provider API Keys</span>
          </Typography>
        </Box>

        {/* System Prompt */}
        <TextField
          label="System Prompt"
          multiline
          rows={agentId === 'report-generator' ? 12 : 8}
          value={config.systemPrompt || ''}
          onChange={(e) => setConfig({ ...config, systemPrompt: e.target.value })}
          fullWidth
          helperText="Instructions for how the AI should behave"
          sx={{
            '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.875rem' },
            '& .MuiInputLabel-root': { color: '#737373' },
            '& .MuiFormHelperText-root': { color: '#737373' }
          }}
        />

        {/* Advanced Settings */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            label="Temperature"
            type="number"
            inputProps={{ min: 0, max: 2, step: 0.1 }}
            value={config.temperature || 0.7}
            onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) })}
            helperText="0 = Deterministic, 2 = Creative"
            sx={{
              '& .MuiInputBase-input': { color: '#fafafa' },
              '& .MuiInputLabel-root': { color: '#737373' },
              '& .MuiFormHelperText-root': { color: '#737373', fontSize: '0.75rem' }
            }}
          />
          <TextField
            label="Max Tokens"
            type="number"
            inputProps={{ min: 100, max: 8000, step: 50 }}
            value={config.maxTokens || 500}
            onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e.target.value) })}
            helperText="Maximum response length"
            sx={{
              '& .MuiInputBase-input': { color: '#fafafa' },
              '& .MuiInputLabel-root': { color: '#737373' },
              '& .MuiFormHelperText-root': { color: '#737373', fontSize: '0.75rem' }
            }}
          />
        </Box>

        {/* Auto-Respond Toggle (only for customer support) */}
        {agentId === 'customer-support' && (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={config.autoRespond || false}
                  onChange={(e) => setConfig({ ...config, autoRespond: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#fafafa' }}>Auto-Respond to Customer Messages</Typography>
                  <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    ⚠️ When enabled, AI will automatically reply to all customer messages without human review.
                    Enable only after thorough testing.
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Job Postings Count (only for career-analyzer) */}
        {agentId === 'career-analyzer' && (
          <TextField
            label="Job Postings to Fetch Per Report"
            type="number"
            inputProps={{ min: 5, max: 50, step: 5 }}
            value={config.jobPostingsToFetch || 10}
            onChange={(e) => setConfig({ ...config, jobPostingsToFetch: parseInt(e.target.value) })}
            helperText="Number of real job postings to analyze (5-50). Higher = more accurate skill requirements."
            sx={{
              '& .MuiInputBase-input': { color: '#fafafa' },
              '& .MuiInputLabel-root': { color: '#737373' },
              '& .MuiFormHelperText-root': { color: '#737373', fontSize: '0.75rem' }
            }}
          />
        )}

        {/* Enabled Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={config.enabled !== false}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              color="success"
            />
          }
          label={<Typography sx={{ color: '#fafafa' }}>Agent Enabled</Typography>}
        />

        {/* Save Button */}
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={() => handleSaveAgentConfig(agentId, config)}
          disabled={saving}
          fullWidth
          sx={{
            background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
            color: '#0B0C10',
            fontWeight: 700,
            fontFamily: 'monospace',
            '&:hover': {
              background: 'linear-gradient(90deg, #00CCC4 0%, #7D00CC 100%)',
            }
          }}
        >
          {saving ? 'Saving...' : `Save ${config.name}`}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{
        bgcolor: 'background.paper',
        borderBottom: '2px solid',
        borderColor: 'divider',
        py: 4,
        px: 3
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}
              >
                👑 Admin Dashboard
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                Manage forecasts, users, and system settings
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <IconButton
                onClick={() => navigate('/admin/inbox')}
                sx={{ color: 'primary.main' }}
              >
                <Badge 
                  badgeContent={unreadMessages} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      boxShadow: '0 0 10px rgba(239,68,68,0.6)',
                    }
                  }}
                >
                  <MailIcon />
                </Badge>
              </IconButton>
              <Button
                onClick={() => navigate('/')}
                variant="outlined"
              >
                ← Back to App
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Stats Cards */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 3,
          mb: 4
        }}>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#171717', border: '1px solid #262626' }}>
            <Typography variant="h3" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 300 }}>
              {stats.totalForecasts}
            </Typography>
            <Typography sx={{ color: '#737373', mt: 1, fontFamily: 'monospace' }}>Total Forecasts</Typography>
          </Paper>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#171717', border: '1px solid #262626' }}>
            <Typography variant="h3" sx={{ color: '#10b981', fontFamily: 'monospace', fontWeight: 300 }}>
              {stats.completedForecasts}
            </Typography>
            <Typography sx={{ color: '#737373', mt: 1, fontFamily: 'monospace' }}>Completed</Typography>
          </Paper>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#171717', border: '1px solid #262626' }}>
            <Typography variant="h3" sx={{ color: '#3b82f6', fontFamily: 'monospace', fontWeight: 300 }}>
              {stats.processingForecasts}
            </Typography>
            <Typography sx={{ color: '#737373', mt: 1, fontFamily: 'monospace' }}>Processing</Typography>
          </Paper>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#171717', border: '1px solid #262626' }}>
            <Typography variant="h3" sx={{ color: '#fafafa', fontFamily: 'monospace', fontWeight: 300 }}>
              {stats.totalUsers}
            </Typography>
            <Typography sx={{ color: '#737373', mt: 1, fontFamily: 'monospace' }}>Total Users</Typography>
          </Paper>
        </Box>

        {/* Tabs */}
        <Paper sx={{ bgcolor: '#171717', border: '1px solid #262626', borderRadius: '12px' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #262626',
              '& .MuiTab-root': {
                color: '#737373',
                fontFamily: 'monospace',
                '&.Mui-selected': {
                  color: '#fafafa'
                }
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#fafafa'
              }
            }}
          >
            <Tab label="Forecasts" />
            <Tab label="Users" />
            <Tab label="Platform Settings" />
            <Tab label="Company" />
            <Tab label="Archetypes" />
            <Tab label="AI Agents" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#fafafa' }} />
                <Typography sx={{ mt: 2, color: '#737373', fontFamily: 'monospace' }}>Loading...</Typography>
              </Box>
            ) : (
              <>
                {/* Forecasts Tab */}
                {activeTab === 0 && (
                  <Box>
                    <Typography variant="h5" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
                      All Forecasts
                    </Typography>
                    {forecasts.length === 0 ? (
                      <Typography sx={{ color: '#737373', textAlign: 'center', py: 4, fontFamily: 'monospace' }}>
                        No forecasts yet
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>User</TableCell>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Age</TableCell>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Status</TableCell>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Payment</TableCell>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Created</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {forecasts.map((forecast) => (
                              <TableRow key={forecast.id}>
                                <TableCell sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.875rem', borderColor: '#262626' }}>
                                  {forecast.userId?.substring(0, 8)}...
                                </TableCell>
                                <TableCell sx={{ color: '#fafafa', fontFamily: 'monospace', borderColor: '#262626' }}>
                                  {forecast.structuredData?.age || 'N/A'}
                                </TableCell>
                                <TableCell sx={{ borderColor: '#262626' }}>
                                  <Chip 
                                    label={forecast.status || 'draft'} 
                                    color={getStatusColor(forecast.status)}
                                    size="small"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                  />
                                </TableCell>
                                <TableCell sx={{ borderColor: '#262626' }}>
                                  <Chip 
                                    label={forecast.paymentStatus || 'pending'} 
                                    color={forecast.paymentStatus === 'completed' ? 'success' : 'warning'}
                                    size="small"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                  />
                                </TableCell>
                                <TableCell sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.875rem', borderColor: '#262626' }}>
                                  {formatDate(forecast.createdAt)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* Users Tab */}
                {activeTab === 1 && (
                  <Box>
                    <Typography variant="h5" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
                      All Users
                    </Typography>
                    {users.length === 0 ? (
                      <Typography sx={{ color: '#737373', textAlign: 'center', py: 4, fontFamily: 'monospace' }}>
                        No users yet
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Email</TableCell>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Name</TableCell>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Role</TableCell>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Created</TableCell>
                              <TableCell sx={{ color: '#737373', fontFamily: 'monospace', borderColor: '#262626' }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {users.map((user) => (
                              <TableRow key={user.id}>
                                <TableCell sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.875rem', borderColor: '#262626' }}>
                                  {user.email}
                                </TableCell>
                                <TableCell sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.875rem', borderColor: '#262626' }}>
                                  {user.displayName || '-'}
                                </TableCell>
                                <TableCell sx={{ borderColor: '#262626' }}>
                                  <Chip 
                                    label={user.role || 'user'} 
                                    color={user.role === 'admin' ? 'secondary' : 'default'}
                                    size="small"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                  />
                                </TableCell>
                                <TableCell sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.875rem', borderColor: '#262626' }}>
                                  {formatDate(user.createdAt)}
                                </TableCell>
                                <TableCell sx={{ borderColor: '#262626' }}>
                                  <Button
                                    size="small"
                                    onClick={() => handleToggleUserRole(user.id, user.role)}
                                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                  >
                                    Make {user.role === 'admin' ? 'User' : 'Admin'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* Platform Settings Tab - API Keys & Pricing */}
                {activeTab === 2 && (
                  <Box>
                    <Typography variant="h5" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
                      ⚙️ Platform Settings
                    </Typography>
                    <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 4, fontSize: '0.9rem' }}>
                      Configure API keys, pricing, and system settings. All API keys are stored server-side and never exposed to the frontend.
                    </Typography>

                    {platformSettingsStatus && (
                      <Alert severity={platformSettingsStatus.type} sx={{ mb: 3 }} onClose={() => setPlatformSettingsStatus(null)}>
                        {platformSettingsStatus.message}
                      </Alert>
                    )}

                    {/* Pricing Section */}
                    <Paper sx={{ p: 3, bgcolor: 'rgba(157,0,255,0.05)', border: '1px solid rgba(157,0,255,0.2)', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#9D00FF', fontFamily: 'monospace', mb: 2 }}>
                        💰 Product Pricing
                      </Typography>
                      <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 3, fontSize: '0.85rem' }}>
                        Set prices for each product. Set to $0 for free mode (skips payment screen).
                      </Typography>
                      
                      {(reportPrice === 0 || careerReportPrice === 0 || bundlePrice === 0) && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            💡 FREE MODE: Products priced at $0 will skip the payment screen entirely.
                          </Typography>
                        </Alert>
                      )}
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                        <TextField
                          label="Cognitive Profile Price"
                          type="number"
                          value={reportPrice}
                          onChange={(e) => setReportPrice(parseInt(e.target.value) || 0)}
                          helperText={reportPrice === 0 ? "FREE - no payment required" : "Price in USD"}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          sx={{
                            '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace' },
                            '& .MuiInputLabel-root': { color: '#737373' },
                            '& .MuiFormHelperText-root': { color: reportPrice === 0 ? '#00FFF5' : '#737373' }
                          }}
                        />
                        <TextField
                          label="Career Report Price"
                          type="number"
                          value={careerReportPrice}
                          onChange={(e) => setCareerReportPrice(parseInt(e.target.value) || 0)}
                          helperText={careerReportPrice === 0 ? "FREE - no payment required" : "Price in USD"}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          sx={{
                            '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace' },
                            '& .MuiInputLabel-root': { color: '#737373' },
                            '& .MuiFormHelperText-root': { color: careerReportPrice === 0 ? '#00FFF5' : '#737373' }
                          }}
                        />
                        <TextField
                          label="Bundle Price"
                          type="number"
                          value={bundlePrice}
                          onChange={(e) => setBundlePrice(parseInt(e.target.value) || 0)}
                          helperText={bundlePrice === 0 ? "FREE - no payment required" : "Both reports"}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          sx={{
                            '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace' },
                            '& .MuiInputLabel-root': { color: '#737373' },
                            '& .MuiFormHelperText-root': { color: bundlePrice === 0 ? '#00FFF5' : '#737373' }
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          startIcon={savingPricing ? <CircularProgress size={16} /> : <SaveIcon />}
                          onClick={handleSavePricing}
                          disabled={savingPricing}
                          sx={{
                            bgcolor: '#9D00FF',
                            color: '#fff',
                            fontFamily: 'monospace',
                            '&:hover': { bgcolor: '#7D00CC' }
                          }}
                        >
                          {savingPricing ? 'Saving...' : 'Save Pricing'}
                        </Button>
                      </Box>
                      
                      {pricingStatus && (
                        <Alert severity={pricingStatus.type} sx={{ mt: 2 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{pricingStatus.message}</Typography>
                        </Alert>
                      )}
                    </Paper>

                    {/* AI API Keys - Centralized */}
                    <Paper sx={{ p: 3, bgcolor: 'rgba(157,0,255,0.05)', border: '1px solid rgba(157,0,255,0.2)', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#9D00FF', fontFamily: 'monospace', mb: 2 }}>
                        🤖 AI Provider API Keys
                      </Typography>
                      <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 3, fontSize: '0.85rem' }}>
                        Configure API keys once here. All AI agents will use these centralized keys based on their selected model.
                      </Typography>
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 2 }}>
                        {/* OpenAI */}
                        <Box>
                          <Typography sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem', mb: 0.5 }}>
                            OpenAI (GPT-4o, GPT-4o-mini)
                          </Typography>
                          <TextField
                            fullWidth
                            type={showOpenaiKey ? 'text' : 'password'}
                            value={openaiApiKey}
                            onChange={(e) => setOpenaiApiKey(e.target.value)}
                            placeholder="sk-..."
                            size="small"
                            sx={{
                              '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem' },
                              '& .MuiInputLabel-root': { color: '#737373' }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowOpenaiKey(!showOpenaiKey)} edge="end" size="small">
                                    {showOpenaiKey ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          {openaiApiKeySaved && (
                            <Typography sx={{ color: '#22c55e', fontFamily: 'monospace', fontSize: '0.7rem', mt: 0.5 }}>
                              ✓ Key configured
                            </Typography>
                          )}
                        </Box>

                        {/* Anthropic */}
                        <Box>
                          <Typography sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem', mb: 0.5 }}>
                            Anthropic (Claude Opus, Sonnet, Haiku)
                          </Typography>
                          <TextField
                            fullWidth
                            type={showAnthropicKey ? 'text' : 'password'}
                            value={anthropicApiKey}
                            onChange={(e) => setAnthropicApiKey(e.target.value)}
                            placeholder="sk-ant-..."
                            size="small"
                            sx={{
                              '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem' },
                              '& .MuiInputLabel-root': { color: '#737373' }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowAnthropicKey(!showAnthropicKey)} edge="end" size="small">
                                    {showAnthropicKey ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          {anthropicApiKeySaved && (
                            <Typography sx={{ color: '#22c55e', fontFamily: 'monospace', fontSize: '0.7rem', mt: 0.5 }}>
                              ✓ Key configured
                            </Typography>
                          )}
                        </Box>

                        {/* xAI (Grok) */}
                        <Box>
                          <Typography sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem', mb: 0.5 }}>
                            xAI (Grok-4) - Web Search Enabled
                          </Typography>
                          <TextField
                            fullWidth
                            type={showXaiKey ? 'text' : 'password'}
                            value={xaiApiKey}
                            onChange={(e) => setXaiApiKey(e.target.value)}
                            placeholder="xai-..."
                            size="small"
                            sx={{
                              '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem' },
                              '& .MuiInputLabel-root': { color: '#737373' }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowXaiKey(!showXaiKey)} edge="end" size="small">
                                    {showXaiKey ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          {xaiApiKeySaved && (
                            <Typography sx={{ color: '#22c55e', fontFamily: 'monospace', fontSize: '0.7rem', mt: 0.5 }}>
                              ✓ Key configured
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          onClick={async () => {
                            setPlatformSaving(true);
                            try {
                              await updatePlatformSettings({ 
                                openaiApiKey, 
                                anthropicApiKey, 
                                xaiApiKey 
                              });
                              setOpenaiApiKeySaved(openaiApiKey);
                              setAnthropicApiKeySaved(anthropicApiKey);
                              setXaiApiKeySaved(xaiApiKey);
                              setPlatformStatus({ type: 'success', message: '✅ AI API Keys saved!' });
                            } catch (error) {
                              setPlatformStatus({ type: 'error', message: '❌ Failed to save AI keys' });
                            } finally {
                              setPlatformSaving(false);
                            }
                          }}
                          disabled={platformSaving}
                          sx={{
                            bgcolor: '#9D00FF',
                            '&:hover': { bgcolor: '#7a00cc' },
                            fontFamily: 'monospace'
                          }}
                        >
                          {platformSaving ? 'Saving...' : 'Save AI Keys'}
                        </Button>
                      </Box>

                      {platformStatus && (
                        <Alert severity={platformStatus.type} sx={{ mt: 2 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{platformStatus.message}</Typography>
                        </Alert>
                      )}
                    </Paper>

                    {/* Stripe Payment Gateway */}
                    <Paper sx={{ p: 3, bgcolor: 'rgba(0,255,245,0.05)', border: '1px solid rgba(0,255,245,0.2)', mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#00FFF5', fontFamily: 'monospace' }}>
                          💳 Stripe Payment Gateway
                        </Typography>
                        
                        {/* Sandbox/Live Toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ 
                            color: stripeMode === 'sandbox' ? '#00FFF5' : '#737373', 
                            fontFamily: 'monospace', 
                            fontSize: '0.85rem',
                            fontWeight: stripeMode === 'sandbox' ? 600 : 400
                          }}>
                            Sandbox
                          </Typography>
                          <Switch
                            checked={stripeMode === 'live'}
                            onChange={(e) => setStripeMode(e.target.checked ? 'live' : 'sandbox')}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF6464' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF6464' },
                              '& .MuiSwitch-track': { backgroundColor: '#00FFF5' }
                            }}
                          />
                          <Typography sx={{ 
                            color: stripeMode === 'live' ? '#FF6464' : '#737373', 
                            fontFamily: 'monospace', 
                            fontSize: '0.85rem',
                            fontWeight: stripeMode === 'live' ? 600 : 400
                          }}>
                            Live
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 2, fontSize: '0.85rem' }}>
                        Get your keys from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ color: '#00FFF5' }}>Stripe Dashboard</a>
                      </Typography>
                      
                      {stripeMode === 'live' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            ⚠️ LIVE MODE - Real payments will be processed. Make sure your keys are correct!
                          </Typography>
                        </Alert>
                      )}
                      
                      {/* Sandbox Keys */}
                      <Box sx={{ 
                        p: 2, 
                        mb: 2, 
                        bgcolor: stripeMode === 'sandbox' ? 'rgba(0,255,245,0.1)' : 'rgba(0,0,0,0.2)',
                        borderRadius: 1,
                        border: stripeMode === 'sandbox' ? '1px solid rgba(0,255,245,0.3)' : '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <Typography sx={{ color: '#00FFF5', fontFamily: 'monospace', mb: 2, fontSize: '0.9rem', fontWeight: 600 }}>
                          🧪 Sandbox (Test) Keys
                        </Typography>
                        
                        {stripeSandboxSecretSaved && (
                          <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 1, fontSize: '0.75rem' }}>
                            Saved: {maskApiKey(stripeSandboxSecretSaved)}
                          </Typography>
                        )}
                        <TextField
                          label="Sandbox Secret Key"
                          fullWidth
                          type={showStripeSandboxSecret ? 'text' : 'password'}
                          value={stripeSandboxSecretKey}
                          onChange={(e) => setStripeSandboxSecretKey(e.target.value)}
                          placeholder="sk_test_..."
                          size="small"
                          sx={{
                            mb: 2,
                            '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem' },
                            '& .MuiInputLabel-root': { color: '#737373' }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowStripeSandboxSecret(!showStripeSandboxSecret)} edge="end" sx={{ color: '#737373' }} size="small">
                                  {showStripeSandboxSecret ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        
                        {stripeSandboxWebhookSaved && (
                          <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 1, fontSize: '0.75rem' }}>
                            Saved: {maskApiKey(stripeSandboxWebhookSaved)}
                          </Typography>
                        )}
                        <TextField
                          label="Sandbox Webhook Secret"
                          fullWidth
                          type={showStripeSandboxWebhook ? 'text' : 'password'}
                          value={stripeSandboxWebhookSecret}
                          onChange={(e) => setStripeSandboxWebhookSecret(e.target.value)}
                          placeholder="whsec_..."
                          size="small"
                          sx={{
                            '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem' },
                            '& .MuiInputLabel-root': { color: '#737373' }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowStripeSandboxWebhook(!showStripeSandboxWebhook)} edge="end" sx={{ color: '#737373' }} size="small">
                                  {showStripeSandboxWebhook ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      
                      {/* Live Keys */}
                      <Box sx={{ 
                        p: 2, 
                        mb: 2, 
                        bgcolor: stripeMode === 'live' ? 'rgba(255,100,100,0.1)' : 'rgba(0,0,0,0.2)',
                        borderRadius: 1,
                        border: stripeMode === 'live' ? '1px solid rgba(255,100,100,0.3)' : '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <Typography sx={{ color: '#FF6464', fontFamily: 'monospace', mb: 2, fontSize: '0.9rem', fontWeight: 600 }}>
                          🔴 Live (Production) Keys
                        </Typography>
                        
                        {stripeLiveSecretSaved && (
                          <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 1, fontSize: '0.75rem' }}>
                            Saved: {maskApiKey(stripeLiveSecretSaved)}
                          </Typography>
                        )}
                        <TextField
                          label="Live Secret Key"
                          fullWidth
                          type={showStripeLiveSecret ? 'text' : 'password'}
                          value={stripeLiveSecretKey}
                          onChange={(e) => setStripeLiveSecretKey(e.target.value)}
                          placeholder="sk_live_..."
                          size="small"
                          sx={{
                            mb: 2,
                            '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem' },
                            '& .MuiInputLabel-root': { color: '#737373' }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowStripeLiveSecret(!showStripeLiveSecret)} edge="end" sx={{ color: '#737373' }} size="small">
                                  {showStripeLiveSecret ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        
                        {stripeLiveWebhookSaved && (
                          <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 1, fontSize: '0.75rem' }}>
                            Saved: {maskApiKey(stripeLiveWebhookSaved)}
                          </Typography>
                        )}
                        <TextField
                          label="Live Webhook Secret"
                          fullWidth
                          type={showStripeLiveWebhook ? 'text' : 'password'}
                          value={stripeLiveWebhookSecret}
                          onChange={(e) => setStripeLiveWebhookSecret(e.target.value)}
                          placeholder="whsec_..."
                          size="small"
                          sx={{
                            '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem' },
                            '& .MuiInputLabel-root': { color: '#737373' }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowStripeLiveWebhook(!showStripeLiveWebhook)} edge="end" sx={{ color: '#737373' }} size="small">
                                  {showStripeLiveWebhook ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip 
                          label={stripeMode === 'live' ? '🔴 LIVE MODE ACTIVE' : '🧪 SANDBOX MODE ACTIVE'} 
                          sx={{ 
                            bgcolor: stripeMode === 'live' ? 'rgba(255,100,100,0.2)' : 'rgba(0,255,245,0.2)',
                            color: stripeMode === 'live' ? '#FF6464' : '#00FFF5',
                            fontFamily: 'monospace',
                            fontWeight: 600
                          }} 
                        />
                        <Button
                          variant="contained"
                          startIcon={savingStripe ? <CircularProgress size={16} /> : <SaveIcon />}
                          onClick={handleSaveStripe}
                          disabled={savingStripe}
                          sx={{
                            bgcolor: '#00FFF5',
                            color: '#000',
                            fontFamily: 'monospace',
                            '&:hover': { bgcolor: '#00CCC5' }
                          }}
                        >
                          {savingStripe ? 'Saving...' : 'Save Stripe Settings'}
                        </Button>
                      </Box>
                      
                      {stripeStatus && (
                        <Alert severity={stripeStatus.type} sx={{ mt: 2 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{stripeStatus.message}</Typography>
                        </Alert>
                      )}
                    </Paper>

                    {/* RapidAPI Key */}
                    <Paper sx={{ p: 3, bgcolor: 'rgba(0,255,245,0.05)', border: '1px solid rgba(0,255,245,0.2)', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#00FFF5', fontFamily: 'monospace', mb: 2 }}>
                        🔍 Job Market Data API
                      </Typography>
                      <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 3, fontSize: '0.85rem' }}>
                        Used for fetching real job postings data for career analysis.
                      </Typography>

                      {rapidApiKeySaved && (
                        <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 1, fontSize: '0.75rem' }}>
                          Saved: {maskApiKey(rapidApiKeySaved)}
                        </Typography>
                      )}
                      <TextField
                        label="API Key"
                        fullWidth
                        type={showRapidApiKey ? 'text' : 'password'}
                        value={rapidApiKey}
                        onChange={(e) => setRapidApiKey(e.target.value)}
                        placeholder="Enter your API key"
                        sx={{
                          mb: 2,
                          '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace', fontSize: '0.85rem' },
                          '& .MuiInputLabel-root': { color: '#737373' }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowRapidApiKey(!showRapidApiKey)} edge="end" sx={{ color: '#737373' }}>
                                {showRapidApiKey ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          startIcon={savingRapidApi ? <CircularProgress size={16} /> : <SaveIcon />}
                          onClick={handleSaveRapidApi}
                          disabled={savingRapidApi}
                          sx={{
                            bgcolor: '#00FFF5',
                            color: '#000',
                            fontFamily: 'monospace',
                            '&:hover': { bgcolor: '#00CCC5' }
                          }}
                        >
                          {savingRapidApi ? 'Saving...' : 'Save API Key'}
                        </Button>
                      </Box>
                      
                      {rapidApiStatus && (
                        <Alert severity={rapidApiStatus.type} sx={{ mt: 2 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{rapidApiStatus.message}</Typography>
                        </Alert>
                      )}
                    </Paper>

                    {/* Developer Options */}
                    <Paper sx={{ p: 3, bgcolor: 'rgba(255,100,100,0.05)', border: '1px solid rgba(255,100,100,0.2)', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#FF6464', fontFamily: 'monospace', mb: 2 }}>
                        🛠️ Developer Options
                      </Typography>
                      <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 3, fontSize: '0.85rem' }}>
                        Debug settings for testing. Changes save automatically.
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={platformSettings?.disableFallback || false}
                            onChange={async (e) => {
                              const newValue = e.target.checked;
                              setPlatformSettings({ ...platformSettings, disableFallback: newValue });
                              // Auto-save developer options
                              try {
                                await updatePlatformSettings({ disableFallback: newValue });
                              } catch (error) {
                                console.error('Failed to save developer option:', error);
                              }
                            }}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF6464' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF6464' }
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography sx={{ color: '#fafafa', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                              Disable Fallback Logic
                            </Typography>
                            <Typography sx={{ color: '#737373', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              When enabled, AI errors will NOT use fallback plans. This helps debug API issues.
                            </Typography>
                          </Box>
                        }
                        sx={{ mb: 2 }}
                      />
                      
                      {platformSettings?.disableFallback && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            ⚠️ Fallback is DISABLED. If the AI API fails, the training plan generation will fail completely instead of using a fallback.
                          </Typography>
                        </Alert>
                      )}
                    </Paper>

                    <Alert severity="success" sx={{ mt: 3 }}>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        <strong>🔒 Secure:</strong> All API keys are stored in Firestore and only accessible server-side via Cloud Functions. 
                        They will never be exposed to the frontend or visible in browser dev tools.
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {/* Company Tab - Branding & Contact */}
                {activeTab === 3 && (
                  <Box>
                    <Typography variant="h5" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
                      🏢 Company Information
                    </Typography>
                    <Typography sx={{ color: '#737373', fontFamily: 'monospace', mb: 4, fontSize: '0.9rem' }}>
                      Configure your company branding, contact information, and display settings.
                    </Typography>
                    
                    {platformStatus && (
                      <Alert severity={platformStatus.type} sx={{ mb: 3 }} onClose={() => setPlatformStatus(null)}>
                        {platformStatus.message}
                      </Alert>
                    )}

                    {platformSettings && (
                      <Box sx={{ display: 'grid', gap: 3 }}>
                        <Paper sx={{ p: 3, bgcolor: 'rgba(157,0,255,0.05)', border: '1px solid rgba(157,0,255,0.2)' }}>
                          <Typography variant="h6" sx={{ color: '#9D00FF', fontFamily: 'monospace', mb: 3 }}>
                            🎨 Branding
                          </Typography>
                          
                          <TextField
                            label="Platform Name"
                            value={platformSettings.platformName || ''}
                            onChange={(e) => setPlatformSettings({ ...platformSettings, platformName: e.target.value })}
                            helperText="Display name shown throughout the app (e.g., 'MindTrace', 'NeurolNet.ai')"
                            fullWidth
                            sx={{
                              mb: 2,
                              '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace' },
                              '& .MuiInputLabel-root': { color: '#737373' },
                              '& .MuiFormHelperText-root': { color: '#737373' }
                            }}
                          />

                          <TextField
                            label="Platform Tagline"
                            value={platformSettings.platformTagline || ''}
                            onChange={(e) => setPlatformSettings({ ...platformSettings, platformTagline: e.target.value })}
                            helperText="Subtitle or tagline (e.g., 'Your mind, decoded from your words.')"
                            fullWidth
                            sx={{
                              '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace' },
                              '& .MuiInputLabel-root': { color: '#737373' },
                              '& .MuiFormHelperText-root': { color: '#737373' }
                            }}
                          />
                        </Paper>

                        <Paper sx={{ p: 3, bgcolor: 'rgba(0,255,245,0.05)', border: '1px solid rgba(0,255,245,0.2)' }}>
                          <Typography variant="h6" sx={{ color: '#00FFF5', fontFamily: 'monospace', mb: 3 }}>
                            📧 Contact Information
                          </Typography>
                          
                          <TextField
                            label="Company Name"
                            value={platformSettings.companyName || ''}
                            onChange={(e) => setPlatformSettings({ ...platformSettings, companyName: e.target.value })}
                            helperText="Legal company name (e.g., 'NeurolNet Inc.')"
                            fullWidth
                            sx={{
                              mb: 2,
                              '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace' },
                              '& .MuiInputLabel-root': { color: '#737373' },
                              '& .MuiFormHelperText-root': { color: '#737373' }
                            }}
                          />
                          
                          <TextField
                            label="Support Email"
                            type="email"
                            value={platformSettings.supportEmail || ''}
                            onChange={(e) => setPlatformSettings({ ...platformSettings, supportEmail: e.target.value })}
                            helperText="Email for customer support inquiries"
                            fullWidth
                            sx={{
                              '& .MuiInputBase-input': { color: '#fafafa', fontFamily: 'monospace' },
                              '& .MuiInputLabel-root': { color: '#737373' },
                              '& .MuiFormHelperText-root': { color: '#737373' }
                            }}
                          />
                        </Paper>

                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<SaveIcon />}
                          onClick={handleSavePlatformSettings}
                          disabled={platformSaving}
                          sx={{
                            background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                            color: '#0B0C10',
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            px: 4,
                            alignSelf: 'flex-end',
                            '&:hover': {
                              background: 'linear-gradient(90deg, #00CCC4 0%, #7D00CC 100%)',
                            }
                          }}
                        >
                          {platformSaving ? 'Saving...' : 'Save Company Info'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Archetypes Tab */}
                {activeTab === 4 && (
                  <Box>
                    <ArchetypesManager />
                  </Box>
                )}

                {/* AI Agents Tab */}
                {activeTab === 5 && (
                  <Box>
                    <Typography variant="h5" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
                      🤖 AI Agents Configuration
                    </Typography>

                    {saveStatus && (
                      <Alert severity={saveStatus.type} sx={{ mb: 3 }} onClose={() => setSaveStatus(null)}>
                        {saveStatus.message}
                      </Alert>
                    )}

                    {/* Sub-tabs for AI Agents */}
                    <Box sx={{ borderBottom: 1, borderColor: '#262626', mb: 3 }}>
                      <Tabs 
                        value={aiAgentTab} 
                        onChange={(e, newValue) => setAiAgentTab(newValue)}
                        sx={{ 
                          '& .MuiTab-root': { 
                            color: '#737373', 
                            fontFamily: 'monospace', 
                            textTransform: 'none',
                            fontSize: '1rem'
                          },
                          '& .Mui-selected': { 
                            color: 'primary.main !important',
                            fontWeight: 600
                          },
                          '& .MuiTabs-indicator': { 
                            backgroundColor: 'primary.main'
                          }
                        }}
                      >
                        <Tab label="Platform Agents" />
                        <Tab label="Cognitive Report Agents" />
                        <Tab label="Career Report Agents" />
                      </Tabs>
                    </Box>

                    {/* Platform Agents Tab */}
                    {aiAgentTab === 0 && (
                      <Box>
                        <Typography variant="h6" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
                          Platform-wide AI Agents
                        </Typography>

                        {/* Customer Support Agent */}
                        {renderAgentCard('customer-support', supportConfig, setSupportConfig, showSupportKey, setShowSupportKey)}

                        {/* Info Box */}
                        <Paper sx={{ p: 3, mt: 4, bgcolor: 'rgba(0,255,245,0.05)', border: '1px solid rgba(0,255,245,0.2)' }}>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2, fontFamily: 'monospace' }}>
                            💡 Platform Agents
                          </Typography>
                          <Box>
                            <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
                              Customer Support Agent (Cost: ~$0.01-0.05 per message)
                            </Typography>
                            <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                              Handles customer inquiries, refunds, and support tickets. Can auto-respond when enabled.
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {/* Cognitive Report Agents Tab */}
                    {aiAgentTab === 1 && (
                      <Box>
                        <Typography variant="h6" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
                          Cognitive Report AI Agents
                        </Typography>

                        {/* Classifier Agent */}
                        {renderAgentCard('classifier', classifierConfig, setClassifierConfig, showClassifierKey, setShowClassifierKey)}

                        <Divider sx={{ my: 5, borderColor: '#262626' }} />

                        {/* Report Generator Agent */}
                        {renderAgentCard('report-generator', reportGeneratorConfig, setReportGeneratorConfig, showReportGeneratorKey, setShowReportGeneratorKey)}

                        {/* Info Box */}
                        <Paper sx={{ p: 3, mt: 4, bgcolor: 'rgba(0,255,245,0.05)', border: '1px solid rgba(0,255,245,0.2)' }}>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2, fontFamily: 'monospace' }}>
                            💡 Cognitive Report Agents
                          </Typography>
                          <Box sx={{ display: 'grid', gap: 2 }}>
                            <Box>
                              <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
                                1. Classifier Agent (Cost: ~$0.01-0.03 per report)
                              </Typography>
                              <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                                Uses GPT-4-mini to quickly classify cognitive patterns from chat logs. Returns structured tags for archetype matching.
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
                                2. Report Generator Agent (Cost: ~$0.15-0.30 per report)
                              </Typography>
                              <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                                Uses Claude Sonnet 4 to generate comprehensive cognitive reports with profound insights. Can be switched to other Claude models or GPT-4 models as needed.
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {/* Career Report Agents Tab */}
                    {aiAgentTab === 2 && (
                      <Box>
                        <Typography variant="h6" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 3, fontWeight: 300 }}>
                          Career Report AI Agents
                        </Typography>

                        {/* Career Path Suggester Agent */}
                        {renderAgentCard('career-suggester', careerSuggesterConfig, setCareerSuggesterConfig, showSuggesterKey, setShowSuggesterKey)}

                        <Divider sx={{ my: 5, borderColor: '#262626' }} />

                        {/* Resume Parser Agent */}
                        {renderAgentCard('resume-parser', resumeParserConfig, setResumeParserConfig, showParserKey, setShowParserKey)}

                        <Divider sx={{ my: 5, borderColor: '#262626' }} />

                        {/* Pivot Feasibility Checker Agent */}
                        {renderAgentCard('pivot-feasibility', pivotFeasibilityConfig, setPivotFeasibilityConfig, showPivotKey, setShowPivotKey)}

                        <Divider sx={{ my: 5, borderColor: '#262626' }} />

                        {/* Career Analyzer Agent */}
                        {renderAgentCard('career-analyzer', careerAnalyzerConfig, setCareerAnalyzerConfig, showCareerAnalyzerKey, setShowCareerAnalyzerKey)}

                        <Divider sx={{ my: 5, borderColor: '#262626' }} />

                        {/* SOC Mapper Agent */}
                        {socMapperConfig && renderAgentCard('soc-mapper', socMapperConfig, setSocMapperConfig, showSocMapperKey, setShowSocMapperKey)}

                        <Divider sx={{ my: 5, borderColor: '#262626' }} />

                        {/* Training Planner Agent (Grok) */}
                        {renderAgentCard('training-planner', trainingPlannerConfig, setTrainingPlannerConfig, showTrainingPlannerKey, setShowTrainingPlannerKey)}

                        <Divider sx={{ my: 5, borderColor: '#262626' }} />

                        {/* Quality Auditor Agent */}
                        {renderAgentCard('quality-auditor', qualityAuditorConfig, setQualityAuditorConfig, showQualityAuditorKey, setShowQualityAuditorKey)}

                        {/* Info Box */}
                        <Paper sx={{ p: 3, mt: 4, bgcolor: 'rgba(0,255,245,0.05)', border: '1px solid rgba(0,255,245,0.2)' }}>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2, fontFamily: 'monospace' }}>
                            💡 Career Report Agents
                          </Typography>
                          <Box sx={{ display: 'grid', gap: 2 }}>
                            <Box>
                              <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
                                1. Career Path Suggester (Cost: ~$0.01-0.02 per suggestion)
                              </Typography>
                              <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                                Uses GPT-4o-mini to analyze resume and suggest 3 logical next career options. Runs during Step 1 of the wizard.
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
                                2. Resume Parser (Cost: ~$0.01-0.03 per resume)
                              </Typography>
                              <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                                Uses GPT-4o-mini to extract structured data (skills, education, experience) from resumes. Runs during Step 2 of the wizard.
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
                                3. Career Analyzer (Cost: ~$0.05-0.15 per report)
                              </Typography>
                              <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                                Uses GPT-4o to analyze job market data and create personalized development plans. Runs after payment to generate the final report.
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
                                4. Training Planner (Cost: ~$0.02-0.05 per plan)
                              </Typography>
                              <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                                Uses Grok-4 with web search to find real courses and certifications. Returns verified URLs instead of hallucinated links.
                              </Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ color: '#fafafa', fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
                                5. Quality Auditor (Cost: ~$0.01-0.02 per audit)
                              </Typography>
                              <Typography sx={{ color: '#737373', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                                Validates training plans for accuracy. Auto-corrects fixable issues, blocks delivery if transition is infeasible. Quality over speed.
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default AdminDashboard;
